/**
 * Spanish Great Controversy — same domain, path prefix `/cs`
 * e.g. lineagejourney.com/cs
 *
 * Prismic:
 * - books uid: la-grande-controversia (override with GATSBY_SPANISH_GC_BOOK_UID)
 * - book_chapter uids: gc-es-chapter1 … (study) or book-grandecontroversia-chapter1 … (full read)
 */

const SPANISH_GC_BOOK_UID = process.env.GATSBY_SPANISH_GC_BOOK_UID || 'la-grande-controversia';

const ENGLISH_GC_BOOK_UID = 'the-great-controversy';

/** Display title for Spanish GC (UI strings) */
const SPANISH_GC_BOOK_TITLE = 'La Gran Controversia';

/** Public URL base for Spanish GC (no trailing slash) */
const SPANISH_GC_BASE_PATH = process.env.GATSBY_SPANISH_GC_BASE_PATH || '/cs';

/** @deprecated separate-domain mode — kept for compatibility, prefer path /cs */
const isSpanishGcSite = () => process.env.GATSBY_SPANISH_GC_SITE === 'true';

const isSpanishGcBook = (uid) => uid === SPANISH_GC_BOOK_UID;

const isSpanishGcChapter = (uid) => {
  const id = String(uid || '');
  return id.startsWith('gc-es-') || id.startsWith('grandecontroversia-chapter');
};

/** Chapter belongs to the Spanish GC book (by uid pattern or Prismic book link) */
const isSpanishGcChapterRecord = (chapter) => {
  const uid = chapter?.uid;
  const bookUid = chapter?.data?.chapter_of_book?.uid;
  return isSpanishGcChapter(uid) || isSpanishGcFullReadChapter(uid) || bookUid === SPANISH_GC_BOOK_UID;
};

/** Dedicated Spanish full-read chapter uids (matches public URL slug) */
const isSpanishGcFullReadChapter = (uid) => String(uid || '').startsWith('book-grandecontroversia-chapter');

/** Dedicated English full-read chapter uids */
const isEnglishGcFullReadChapter = (uid) => String(uid || '').startsWith('book-greatcontroversy-chapter');

const isDedicatedGcFullReadChapter = (uid) => isSpanishGcFullReadChapter(uid) || isEnglishGcFullReadChapter(uid);

const isSpanishGcContent = (uid) => isSpanishGcChapter(uid) || isSpanishGcFullReadChapter(uid);

/**
 * Sidebar / chapter nav lists: Prismic book links alone are not enough — drop docs
 * whose uid belongs to the other locale (e.g. book-grandecontroversia on English).
 */
const filterChapterNodesForGcLocale = (nodes = [], isSpanish, { fullRead = false } = {}) =>
  nodes.filter((chapter) => {
    const uid = chapter?.uid;
    if (isSpanish) return isSpanishGcContent(uid);
    if (fullRead) return isEnglishGcFullReadChapter(uid);
    return !isSpanishGcContent(uid);
  });

const getSpanishChapterNumber = (uid) => {
  const match = String(uid || '').match(/chapter(\d+)$/i);
  return match ? Number(match[1]) : null;
};

/** Chapter label number from uid (e.g. gc-chapter6 → 6), not list index. */
const getChapterDisplayNumber = (uid, fallbackIndex = 0) => {
  const chapterNum = getSpanishChapterNumber(uid);
  return chapterNum !== null ? chapterNum : fallbackIndex + 1;
};

/** Sort GC chapter nodes by chapter number extracted from uid */
const sortChaptersByNumber = (chapters = []) =>
  [...chapters].sort((a, b) => {
    const numA = getSpanishChapterNumber(a?.uid) ?? Number.MAX_SAFE_INTEGER;
    const numB = getSpanishChapterNumber(b?.uid) ?? Number.MAX_SAFE_INTEGER;
    return numA - numB;
  });

/** Find chapter index by uid, falling back to chapter number when uid patterns differ */
const findChapterIndex = (chapters, uid) => {
  if (!uid || !chapters?.length) return -1;
  const directIndex = chapters.findIndex((ch) => ch.uid === uid);
  if (directIndex >= 0) return directIndex;
  const chapterNum = getSpanishChapterNumber(uid);
  if (chapterNum === null) return -1;
  return chapters.findIndex((ch) => getSpanishChapterNumber(ch.uid) === chapterNum);
};

/** Find chapter node by chapter number */
const findChapterByNumber = (chapters, chapterNum) =>
  chapters?.find((ch) => getSpanishChapterNumber(ch.uid) === chapterNum) ?? null;

/**
 * Build map of chapter number → dedicated full-read uid
 * e.g. 1 → book-grandecontroversia-chapter1
 * Pass isSpanish to keep English and Spanish full-read docs from overwriting each other.
 */
const buildFullReadUidMap = (chapters = [], isSpanish) => {
  const map = new Map();
  chapters.forEach((chapter) => {
    const num = getSpanishChapterNumber(chapter?.uid);
    if (num === null || !isDedicatedGcFullReadChapter(chapter.uid)) return;
    if (typeof isSpanish === 'boolean') {
      const uidIsSpanish = isSpanishGcFullReadChapter(chapter.uid);
      if (isSpanish !== uidIsSpanish) return;
    }
    map.set(num, chapter.uid);
  });
  return map;
};

/** Full-read uid for a study chapter when a dedicated doc exists in Prismic */
const getDedicatedFullReadUid = (studyUid, fullReadUidByNumber, isSpanish) => {
  const chapterNum = getSpanishChapterNumber(studyUid);
  if (chapterNum === null || !fullReadUidByNumber?.has(chapterNum)) return null;
  const fullReadUid = fullReadUidByNumber.get(chapterNum);
  if (typeof isSpanish === 'boolean') {
    const uidIsSpanish = isSpanishGcFullReadChapter(fullReadUid);
    if (isSpanish !== uidIsSpanish) return null;
  }
  return fullReadUid;
};

/**
 * Use the full-read page when a dedicated doc exists, or for chapters 1–4.
 * Otherwise fall back to the read modal (book_text on the study chapter).
 */
const shouldUseFullReadPage = ({ chapterIndex, dedicatedFullReadUid }) => {
  if (dedicatedFullReadUid) return true;
  return chapterIndex < 4;
};

const shouldShowReadAction = ({ chapterIndex, chapterText, dedicatedFullReadUid }) => {
  if (shouldUseFullReadPage({ chapterIndex, dedicatedFullReadUid })) return true;
  return !!chapterText;
};

/** Normalize uid to Spanish study uid — only when source is English gc-chapterN */
const resolveSpanishChapterUid = (uid) => {
  if (isSpanishGcContent(uid)) return uid;
  if (/^gc-chapter\d+$/i.test(String(uid || ''))) {
    const chapterNum = getSpanishChapterNumber(uid);
    if (chapterNum !== null) return `gc-es-chapter${chapterNum}`;
  }
  return uid;
};

/** Study chapter path on Spanish GC pages */
const getSpanishStudyChapterPath = (uid) => getChapterPublicPath(resolveSpanishChapterUid(uid));

/** Study chapter path — respects Spanish vs English context */
const getStudyChapterNavigatePath = (uid, isSpanishContext = false) => {
  if (isSpanishContext || isSpanishGcChapter(uid)) {
    return getSpanishStudyChapterPath(uid);
  }
  return getChapterPublicPath(uid);
};

/** Detect Spanish GC from current URL (client-side navigation fallback) */
const isSpanishGcPathname = (pathname) => String(pathname || '').startsWith(SPANISH_GC_BASE_PATH);

/** Resolve public path for Spanish chapter navigation (prefers dedicated full-read doc) */
const getSpanishNavigationChapterPath = (chapterUid, fullReadUidByNumber) => {
  const spanishUid = resolveSpanishChapterUid(chapterUid);
  const chapterNum = getSpanishChapterNumber(spanishUid);
  if (chapterNum !== null && fullReadUidByNumber?.has(chapterNum)) {
    return getFullChapterPublicPath(fullReadUidByNumber.get(chapterNum));
  }
  return getFullChapterPublicPath(spanishUid);
};

/**
 * Chapter path segment (no leading slash, no /cs prefix)
 * gc-es-chapter1 → grandecontroversia-chapter1
 * gc-chapter1 → greatcontroversy-chapter1
 */
const getChapterPublicSlug = (uid) => {
  if (!uid) return '';
  const id = String(uid);
  if (id.startsWith('gc-es-')) return id.replace(/^gc-es-/, 'grandecontroversia-');
  if (id.startsWith('grandecontroversia-chapter')) return id;
  return id.replace(/^gc-/, 'greatcontroversy-');
};

/**
 * Full public path for a study chapter page
 * gc-es-chapter1 → /cs/grandecontroversia-chapter1
 * grandecontroversia-chapter1 → /cs/grandecontroversia-chapter1
 * gc-chapter1 → /greatcontroversy-chapter1 (use getStudyChapterNavigatePath for Spanish book)
 */
const getChapterPublicPath = (uid) => {
  const slug = getChapterPublicSlug(uid);
  if (!slug) return '/';
  if (isSpanishGcChapter(uid)) {
    return `${SPANISH_GC_BASE_PATH}/${slug}`;
  }
  return `/${slug}`;
};

/**
 * Full-read chapter path
 * gc-es-chapter1 → /cs/book-grandecontroversia-chapter1
 */
const getFullChapterPublicPath = (uid) => {
  if (!uid) return '/';
  const id = String(uid);
  if (isSpanishGcFullReadChapter(id)) {
    return `${SPANISH_GC_BASE_PATH}/${id}`;
  }
  if (isEnglishGcFullReadChapter(id)) {
    return `/${id}`;
  }
  if (id.startsWith('grandecontroversia-chapter')) {
    return `${SPANISH_GC_BASE_PATH}/${id.replace(/^grandecontroversia-/, 'book-grandecontroversia-')}`;
  }
  if (id.startsWith('gc-es-')) {
    return `${SPANISH_GC_BASE_PATH}/${id.replace(/^gc-es-/, 'book-grandecontroversia-')}`;
  }
  return `/${id.replace(/^gc-/, 'book-greatcontroversy-')}`;
};

const getSpanishBookPath = () => SPANISH_GC_BASE_PATH;

const ENGLISH_GC_BOOK_PATH = '/books/the-great-controversy';

/** True when rendering a Spanish GC page (study or full-read) */
const isSpanishGcPage = (pageContext, uid) =>
  pageContext?.spanishGc === true ||
  pageContext?.locale === 'es' ||
  pageContext?.bookUid === SPANISH_GC_BOOK_UID ||
  isSpanishGcContent(uid);

/**
 * Header back-link target for GC chapter pages.
 * English full-read → matching study chapter (e.g. /greatcontroversy-chapter5).
 * English study → GC book landing.
 * Spanish full-read and study → Spanish book home (/cs).
 */
const getGcBackPath = (pageContext, uid) => {
  if (!isSpanishGcPage(pageContext, uid)) {
    if (isEnglishGcFullReadChapter(uid)) {
      const chapterNum = getSpanishChapterNumber(uid);
      if (chapterNum !== null) return getChapterPublicPath(`gc-chapter${chapterNum}`);
    }
    return ENGLISH_GC_BOOK_PATH;
  }

  return getSpanishBookPath();
};

/** Resolve GC back path from pageContext, uid, or current URL (client fallback) */
const resolveGcBackPath = (pageContext, uid, pathname) => {
  const path = String(pathname || '');
  const spanishBase = SPANISH_GC_BASE_PATH.replace(/\/$/, '') || '/cs';

  if (path === spanishBase || path === `${spanishBase}/`) {
    return '/';
  }

  if (isSpanishGcPage(pageContext, uid) || path.startsWith(`${spanishBase}/`)) {
    return getGcBackPath(pageContext, uid);
  }

  const englishFullReadMatch = path.match(/^\/book-greatcontroversy-chapter(\d+)\/?$/i);
  if (englishFullReadMatch) {
    return getChapterPublicPath(`gc-chapter${englishFullReadMatch[1]}`);
  }

  if (uid) {
    return getGcBackPath(pageContext, uid);
  }

  return ENGLISH_GC_BOOK_PATH;
};

const GC_UI = {
  en: {
    home: 'Home /',
    books: 'Books /',
    featuredBooks: 'FEATURED BOOKS',
    chapters: 'CHAPTERS',
    chapterLabel: (n) => `Chapter ${n}`,
    minRead: 'MIN READ',
    completed: 'Completed',
    signupPromptBefore: 'Please',
    signup: 'sign up',
    signupOr: 'or',
    login: 'log in',
    signupPromptAfter: 'to track and display your read progress.',
    signupBtn: 'SIGN UP',
    share: 'Share',
    pageTitleFallback: 'The Great Controversy',
    bookSubtitle: 'Learn about the lineage of freedom and what its future will be.',
    chapterPageHeader: (n) => `The Great Controversy | Chapter ${n}`,
    readProgressLabel: 'Read',
    readProgressOf: 'of',
    chaptersNavLabel: 'chapters',
    chaptersNavShort: 'CH',
    chAbbrev: (n) => `Ch. ${n}`,
    signInToContinue: 'Sign in to continue',
    voicePrompt: 'Choose the right voice for your listening experience',
    seoTitleSuffix: 'The Great Controversy',
    readModalChapter: (n, title) => `Chapter ${n} - ${title}`,
    read: 'Read',
    listen: 'Listen',
    listenLoading: 'Loading...',
    listenPause: 'Pause Audio',
    listenResume: 'Resume Audio',
    authorTitle: 'About the Author',
    authorDescription:
      'The Great Controversy was written by Ellen G. White (1827–1915), a prolific author and influential Christian thinker. She produced thousands of pages on theology, health, education, and daily life. Her writings continue to inspire millions worldwide, emphasizing faith in Christ, holistic living, and practical spirituality.',
    authorImageAlt: 'Ellen G. White - Book Author',
    readChapterIn: (title) => `READ CHAPTER IN ${title}`,
    readCompleteChapterLabel: 'Read the complete chapter',
    readMore: 'READ MORE',
    furtherReading: 'Further Reading',
    footnotes: 'Footnotes',
    discussionQuestions: 'Discussion Questions',
    nextChapter: 'Next Chapter',
    next: 'Next',
    previous: 'Previous',
    relatedContents: 'Related contents',
    shareChapter: 'Share Chapter',
    readChapter: 'Read Chapter',
    listenToChapter: 'Listen to Chapter',
    chooseVoiceShort: 'Choose the right voice:',
    quizQuestions: 'Quiz Questions',
    quizResult: 'Result',
    questionLabel: (n) => `Question ${n}`,
    quizPrevious: 'Previous',
    quizSeeResult: 'See Result',
    quizAnsweredCorrectly: (score, total) => `You answered ${score} out of ${total} questions correctly.`,
    quizExcellent: 'Excellent! 🎉',
    quizGreatWork: 'Great work! 😊',
    quizWellDone: 'Well done! 😄',
    quizKeepGoing: 'Keep going — every journey starts somewhere. 👍',
    quizRevisitQuestions: 'Revisit Questions',
    quizTryAgain: 'Try Again',
    contactUs: 'Contact Us',
    contactHeading: "Got a question or something you're wondering about? Send it our way—",
    contactHighlight: "we'd love to hear from you.",
    contactFirstName: 'First Name',
    contactEmail: 'Email',
    contactMessage: 'Message',
    contactFirstNamePlaceholder: 'Enter first name',
    contactEmailPlaceholder: 'Enter your email',
    contactMessagePlaceholder: 'Enter your message...',
    contactSendMessage: 'SEND MESSAGE',
    contactSending: 'SENDING...',
    contactTermsBefore: 'By sending message you agree to the Lineage Journey',
    contactPrivacyPolicy: 'privacy policy',
    contactSuccessTitle: 'Message sent!',
    contactSuccessBody: "Thanks for reaching out. We'll get back to you soon.",
    contactSendAnother: 'SEND ANOTHER',
    contactToastSuccess: 'Message sent successfully!',
    contactToastError: 'Failed to send message. Please try again.',
    featureBooksTitle: 'Feature Books',
    featureBooksSubtitle: 'Explore our handpicked selections for your spiritual journey.',
    purchaseNow: 'PURCHASE NOW',
    illustratedGcTitle: 'The Illustrated Great Controversy',
    illustratedGcNowInSpanish: 'Now available in Spanish',
    spanishIllustratedGcTitle: 'El Conflicto De Los Siglos',
    shareableGcFeatureTitle: 'The Great Controversy',
    shareableGcFeatureEdition: '(Full Colour Shareable Edition)',
    gcTitle: 'The Great Controversy',
    sections: 'SECTIONS',
    sidebarChapterSummary: 'Chapter summary',
    chapterSummaryLabel: 'Chapter Summary',
    chapterSummaryBy: 'by',
    chapterSummaryAuthor: 'Sukeshinie Goonatilleke',
    sidebarMap: 'Map',
    sidebarFeatureBooks: 'Feature Books',
    sidebarAdditionalVideos: 'Additional Videos',
    sidebarQuiz: 'Quiz Questions',
    sidebarAppendix: 'Appendix',
    sidebarContact: 'Got a question?',
    sidebarChapters: 'CHAPTERS',
    sidebarRead: 'Read',
    prevShort: 'Prev.',
    nextShort: 'Next.',
    viewTimeline: 'View timeline',
    nextVideo: 'Next Video',
    furtherListening: 'Further Listening',
    chaptersLower: 'chapters',
    getFreeCopy: 'Get Free Copy',
    illustratedGcLinkSub: 'This book is a beautifully illustrated story of faith, courage, and hope.',
    freeGcLinkSub: 'Full books are available as a physical copy.',
    categoryWatch: 'WATCH',
    categoryRead: 'READ',
    categoryListen: 'LISTEN',
  },
  es: {
    home: 'Inicio /',
    books: 'Libros /',
    featuredBooks: 'LIBROS DESTACADOS',
    chapters: 'CAPÍTULOS',
    chapterLabel: (n) => `Capítulo ${n}`,
    minRead: 'MIN DE LECTURA',
    completed: 'Completado',
    signupPromptBefore: 'Por favor',
    signup: 'regístrate',
    signupOr: 'o',
    login: 'inicia sesión',
    signupPromptAfter: 'para guardar y ver tu progreso de lectura.',
    signupBtn: 'REGISTRARSE',
    share: 'Compartir',
    pageTitleFallback: SPANISH_GC_BOOK_TITLE,
    bookSubtitle: 'Conozca el linaje de la libertad y cuál será su futuro.',
    chapterPageHeader: (n) => `${SPANISH_GC_BOOK_TITLE} | Capítulo ${n}`,
    readProgressLabel: 'Leído',
    readProgressOf: 'de',
    chaptersNavLabel: 'capítulos',
    chaptersNavShort: 'CAP',
    chAbbrev: (n) => `Cap. ${n}`,
    signInToContinue: 'Inicia sesión para continuar',
    voicePrompt: 'Elige la voz adecuada para tu experiencia de escucha',
    seoTitleSuffix: SPANISH_GC_BOOK_TITLE,
    readModalChapter: (n, title) => `Capítulo ${n} - ${title}`,
    read: 'Leer',
    listen: 'Escuchar',
    listenLoading: 'Cargando...',
    listenPause: 'Pausar audio',
    listenResume: 'Reanudar audio',
    authorTitle: 'Sobre la autora',
    authorDescription: `${SPANISH_GC_BOOK_TITLE} fue escrito por Ellen G. White (1827–1915), una prolífica autora e influyente pensadora cristiana. Produjo miles de páginas sobre teología, salud, educación y la vida cotidiana. Sus escritos continúan inspirando a millones de personas en todo el mundo, enfatizando la fe en Cristo, una vida integral y la espiritualidad práctica.`,
    authorImageAlt: 'Ellen G. White - Autora',
    readChapterIn: () => `LEER CAPÍTULO EN ${SPANISH_GC_BOOK_TITLE}`,
    readCompleteChapterLabel: 'Lee el capítulo completo',
    readMore: 'LEER MÁS',
    furtherReading: 'Lecturas adicionales',
    footnotes: 'Notas al pie',
    discussionQuestions: 'Preguntas de discusión',
    nextChapter: 'Siguiente capítulo',
    next: 'Siguiente',
    previous: 'Anterior',
    relatedContents: 'Contenido relacionado',
    shareChapter: 'Compartir capítulo',
    readChapter: 'Leer capítulo',
    listenToChapter: 'Escuchar capítulo',
    chooseVoiceShort: 'Elige la voz adecuada:',
    quizQuestions: 'Preguntas del cuestionario',
    quizResult: 'Resultado',
    questionLabel: (n) => `Pregunta ${n}`,
    quizPrevious: 'Anterior',
    quizSeeResult: 'Ver resultado',
    quizAnsweredCorrectly: (score, total) => `Respondiste correctamente ${score} de ${total} preguntas.`,
    quizExcellent: '¡Excelente! 🎉',
    quizGreatWork: '¡Buen trabajo! 😊',
    quizWellDone: '¡Muy bien! 😄',
    quizKeepGoing: 'Sigue adelante — todo camino comienza en algún lugar. 👍',
    quizRevisitQuestions: 'Revisar preguntas',
    quizTryAgain: 'Intentar de nuevo',
    contactUs: 'Contáctanos',
    contactHeading: '¿Tienes una pregunta o algo en lo que estés pensando? Envíanosla—',
    contactHighlight: 'nos encantaría saber de ti.',
    contactFirstName: 'Nombre',
    contactEmail: 'Correo electrónico',
    contactMessage: 'Mensaje',
    contactFirstNamePlaceholder: 'Ingresa tu nombre',
    contactEmailPlaceholder: 'Ingresa tu correo electrónico',
    contactMessagePlaceholder: 'Ingresa tu mensaje...',
    contactSendMessage: 'ENVIAR MENSAJE',
    contactSending: 'ENVIANDO...',
    contactTermsBefore: 'Al enviar el mensaje, aceptas la',
    contactPrivacyPolicy: 'política de privacidad',
    contactSuccessTitle: '¡Mensaje enviado!',
    contactSuccessBody: 'Gracias por contactarnos. Te responderemos pronto.',
    contactSendAnother: 'ENVIAR OTRO',
    contactToastSuccess: '¡Mensaje enviado con éxito!',
    contactToastError: 'No se pudo enviar el mensaje. Inténtalo de nuevo.',
    featureBooksTitle: 'Libros destacados',
    featureBooksSubtitle: 'Explora nuestra selección especial para tu camino espiritual.',
    purchaseNow: 'COMPRAR AHORA',
    illustratedGcTitle: 'La Gran Controversia Ilustrada',
    illustratedGcNowInSpanish: 'Ya disponible en español',
    spanishIllustratedGcTitle: 'El Conflicto De Los Siglos',
    shareableGcFeatureTitle: 'La Gran Controversia',
    shareableGcFeatureEdition: '(Edición compartible a todo color)',
    gcTitle: SPANISH_GC_BOOK_TITLE,
    sections: 'SECCIONES',
    sidebarChapterSummary: 'Resumen del capítulo',
    chapterSummaryLabel: 'Resumen del capítulo',
    chapterSummaryBy: 'por',
    chapterSummaryAuthor: 'Sukeshinie Goonatilleke',
    sidebarMap: 'Mapa',
    sidebarFeatureBooks: 'Libros destacados',
    sidebarAdditionalVideos: 'Videos adicionales',
    sidebarQuiz: 'Preguntas del cuestionario',
    sidebarAppendix: 'Apéndice',
    sidebarContact: '¿Tienes una pregunta?',
    sidebarChapters: 'CAPÍTULOS',
    sidebarRead: 'Leído',
    prevShort: 'Ant.',
    nextShort: 'Sig.',
    viewTimeline: 'Ver línea de tiempo',
    nextVideo: 'Siguiente video',
    furtherListening: 'Para escuchar',
    chaptersLower: 'capítulos',
    getFreeCopy: 'Obtener copia gratuita',
    illustratedGcLinkSub: 'Este libro es una hermosa historia ilustrada de fe, valor y esperanza.',
    freeGcLinkSub: 'Los libros completos están disponibles en copia física.',
    categoryWatch: 'VER',
    categoryRead: 'LEER',
    categoryListen: 'ESCUCHAR',
  },
};

const getGcUi = (localeOrBookUid) => {
  if (localeOrBookUid === 'es' || localeOrBookUid === 'cs' || isSpanishGcBook(localeOrBookUid)) {
    return GC_UI.es;
  }
  return GC_UI.en;
};

/** Normalize redirect query param to a safe in-site path */
const resolveAuthRedirect = (redirect) => {
  if (!redirect) return null;
  const path = redirect.startsWith('/') ? redirect : `/${redirect}`;
  if (!path.startsWith('/') || path.startsWith('//')) return null;
  return path;
};

/** Login URL that returns the user to `returnPath` after sign-in */
const getLoginPath = (returnPath) => {
  if (!returnPath) return '/login';
  return `/login?redirect=${encodeURIComponent(returnPath)}`;
};

/** Signup URL that returns the user to `returnPath` after account creation */
const getSignupPath = (returnPath) => {
  if (!returnPath) return '/signup';
  return `/signup?redirect=${encodeURIComponent(returnPath)}`;
};

/** Best return path for auth flows on Spanish GC pages */
const getGcAuthReturnPath = (pathname) => {
  if (pathname && pathname.startsWith(SPANISH_GC_BASE_PATH)) {
    return pathname;
  }
  return getSpanishBookPath();
};

module.exports = {
  SPANISH_GC_BOOK_UID,
  ENGLISH_GC_BOOK_UID,
  SPANISH_GC_BOOK_TITLE,
  SPANISH_GC_BASE_PATH,
  isSpanishGcSite,
  isSpanishGcBook,
  isSpanishGcChapter,
  isSpanishGcChapterRecord,
  isSpanishGcFullReadChapter,
  isEnglishGcFullReadChapter,
  isDedicatedGcFullReadChapter,
  isSpanishGcContent,
  filterChapterNodesForGcLocale,
  getSpanishChapterNumber,
  getChapterDisplayNumber,
  sortChaptersByNumber,
  findChapterIndex,
  findChapterByNumber,
  buildFullReadUidMap,
  getDedicatedFullReadUid,
  shouldUseFullReadPage,
  shouldShowReadAction,
  getSpanishNavigationChapterPath,
  resolveSpanishChapterUid,
  getSpanishStudyChapterPath,
  getStudyChapterNavigatePath,
  isSpanishGcPathname,
  getChapterPublicSlug,
  getChapterPublicPath,
  getFullChapterPublicPath,
  getSpanishBookPath,
  ENGLISH_GC_BOOK_PATH,
  isSpanishGcPage,
  getGcBackPath,
  resolveGcBackPath,
  GC_UI,
  getGcUi,
  resolveAuthRedirect,
  getLoginPath,
  getSignupPath,
  getGcAuthReturnPath,
};
